import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {AiChatSource, ChatMessage, PointedAtEntity} from './ai-chat-window.types';
import {omitKey} from '@Utils/record.util';

export interface ChatState {
  messages: Record<string, ChatMessage>;
  isStreaming: boolean;
  error: string | null;
  isRefreshing: boolean;
  hasWriteAccess: boolean;
  isDeleted: boolean;
  source: AiChatSource | null;
  turnStartedAt: number | null;
  pointedAtEntity: PointedAtEntity | null;
}

export type AiChatWindowReduxState = Record<string, ChatState | undefined>;

const resolveTurnStartedAt = (currentTurnStartedAt: number | null, isStreaming: boolean): number | null => {
  if (!isStreaming) {
    return null;
  }

  if (currentTurnStartedAt) {
    return currentTurnStartedAt;
  }

  return Date.now();
};

export const createDefaultChatState = (): ChatState => ({messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, source: null, turnStartedAt: null, pointedAtEntity: null});

interface SetMessagesAction {
  chatId: string;
  messages: Record<string, ChatMessage>;
}

interface SetIsStreamingAction {
  chatId: string;
  isStreaming: boolean;
}

interface SetErrorAction {
  chatId: string;
  error: string | null;
}

interface UpdateMessageAction {
  chatId: string;
  messageId: string;
  message: ChatMessage;
}

interface SetIsRefreshingAction {
  chatId: string;
  isRefreshing: boolean;
}

interface SetHasWriteAccessAction {
  chatId: string;
  hasWriteAccess: boolean;
}

interface ClearChatStateAction {
  chatId: string;
}

interface SetIsDeletedAction {
  chatId: string;
  isDeleted: boolean;
}

interface SetPointedAtEntityAction {
  chatId: string;
  pointedAtEntity: PointedAtEntity | null;
}

interface SetSourceAction {
  chatId: string;
  source: AiChatSource | null;
}

const aiChatWindowSlice = createSlice({
  name: 'aiChatWindow',
  initialState: {} as AiChatWindowReduxState,
  reducers: {
    setMessages: (state: AiChatWindowReduxState, action: PayloadAction<SetMessagesAction>) => {
      const {chatId, messages} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].messages = messages;
    },
    updateMessage: (state: AiChatWindowReduxState, action: PayloadAction<UpdateMessageAction>) => {
      const {chatId, messageId, message} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].messages[messageId] = message;
    },
    setIsStreaming: (state: AiChatWindowReduxState, action: PayloadAction<SetIsStreamingAction>) => {
      const {chatId, isStreaming} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].isStreaming = isStreaming;
      state[chatId].turnStartedAt = resolveTurnStartedAt(state[chatId].turnStartedAt, isStreaming);
    },
    setError: (state: AiChatWindowReduxState, action: PayloadAction<SetErrorAction>) => {
      const {chatId, error} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].error = error;
    },
    setIsRefreshing: (state: AiChatWindowReduxState, action: PayloadAction<SetIsRefreshingAction>) => {
      const {chatId, isRefreshing} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].isRefreshing = isRefreshing;
    },
    setHasWriteAccess: (state: AiChatWindowReduxState, action: PayloadAction<SetHasWriteAccessAction>) => {
      const {chatId, hasWriteAccess} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].hasWriteAccess = hasWriteAccess;
    },
    setIsDeleted: (state: AiChatWindowReduxState, action: PayloadAction<SetIsDeletedAction>) => {
      const {chatId, isDeleted} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].isDeleted = isDeleted;
    },
    setSource: (state: AiChatWindowReduxState, action: PayloadAction<SetSourceAction>) => {
      const {chatId, source} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].source = source;
    },
    setPointedAtEntity: (state: AiChatWindowReduxState, action: PayloadAction<SetPointedAtEntityAction>) => {
      const {chatId, pointedAtEntity} = action.payload;
      state[chatId] ??= createDefaultChatState();
      state[chatId].pointedAtEntity = pointedAtEntity;
    },
    clearChatState: (state: AiChatWindowReduxState, action: PayloadAction<ClearChatStateAction>) => {
      const {chatId} = action.payload;
      return omitKey(state, chatId);
    },
  },
});

export const aiChatWindowReducer = aiChatWindowSlice.reducer;
export const aiChatWindowActions = aiChatWindowSlice.actions;
